/**
 * Convert a string to snake_case identifier.
 * Transforms "Hello World" to "hello_world"
 * Transforms "qsd / qsdq" to "qsd_qsdq"
 * Transforms "Héllo Wörld! @#$%" to "hello_world"
 *
 * @param value - The string to convert
 * @returns The string in snake_case
 */
export function toSnakeCase(value: string): string {
  return value
    .toString()
    .normalize('NFKD')
    .toLowerCase()
    .trim()
    .replace(/\s*\/\s*/g, '_')    // Replace " / " or "/" with _
    .replace(/-/g, '_')            // Replace - with _
    .replace(/\s+/g, '_')          // Replace spaces with _
    .replace(/[^a-z0-9_]/gi, '')   // Remove all non-word chars except _
    .replace(/_+/g, '_')           // Replace multiple _ with single _
    .replace(/^_/, '')             // Remove leading _
    .replace(/_$/, '');            // Remove trailing _
}

/**
 * Make a name unique by adding a suffix _1, _2, etc. if it already exists.
 *
 * @param baseName - The base name to make unique
 * @param existingNames - Array of existing names to check against
 * @param currentName - Optional current name (for edit mode, to allow keeping the same name)
 * @returns A unique name
 */
export function makeUniqueName(
  baseName: string,
  existingNames: string[],
  currentName: string | null = null
): string {
  if (!baseName) return '';

  // If the name is the same as the current name (editing), keep it
  if (currentName && baseName === currentName) {
    return baseName;
  }

  // If the name doesn't exist, return it as-is
  if (!existingNames.includes(baseName)) {
    return baseName;
  }

  // Otherwise, add a suffix
  let suffix = 1;
  let uniqueName = `${baseName}_${suffix}`;
  while (existingNames.includes(uniqueName) && uniqueName !== currentName) {
    suffix++;
    uniqueName = `${baseName}_${suffix}`;
  }
  return uniqueName;
}

/**
 * Generate a unique UUID using crypto.randomUUID() with a fallback for older browsers.
 * This is more robust than Math.random() and prevents potential UUID collisions.
 *
 * @returns A UUID string in the format xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 */
export function generateUUID(): string {
  try {
    // Use native crypto.randomUUID if available (modern browsers)
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    // Fallback for older browsers using crypto.getRandomValues
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      // Generate 16 random bytes upfront for efficiency
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      
      // Set version (4) and variant bits according to RFC 4122
      bytes[6] = (bytes[6] & 0x0f) | 0x40; // Version 4
      bytes[8] = (bytes[8] & 0x3f) | 0x80; // Variant 10
      
      // Convert to UUID string format
      const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
      return `${hex.substring(0, 8)}-${hex.substring(8, 12)}-${hex.substring(12, 16)}-${hex.substring(16, 20)}-${hex.substring(20, 32)}`;
    }
  } catch (error) {
    // Fallback to Math.random if crypto API throws SecurityError (e.g., in insecure contexts)
    // or any other error
  }

  // Final fallback using Math.random (for insecure contexts or older browsers)
  // This is better than the previous implementation but still not ideal
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

